import {
  Button,
  OutlinedSelect,
  Select,
  TextOutlinedInput,
} from '@octano/global-ui';

import { Row, Col } from 'reactstrap';
import { useObservationTexts } from '../hooks/useObservationTexts';
import { useRiskObservationsFormLoader } from '../loaders/StudentRiskObservationsFormLoader';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRiskObservationsStudentListLoader } from '../loaders/StudentRiskObservationsStudentListLoader';
import { StudentRiskObservationsFiltersFormFields } from '../types';

export function StudentRiskObservationsFilters({
  defaultPagination,
}: {
  defaultPagination: { items_per_page: number; page: number };
}) {
  const texts = useObservationTexts();
  const { description, filters, actions } = texts;
  const { result, loading } = useRiskObservationsFormLoader();
  const { updateQuery } = useRiskObservationsStudentListLoader();
  const data = result?.success ? result.data : undefined;
  const methods = useFormContext();

  const selectedSchool = methods.watch('school');

  const schoolOptions = useMemo(() => {
    return (
      data?.schools?.map((school) => ({
        label: school.name,
        value: school.id,
      })) ?? []
    );
  }, [data]);

  const studyPlanOptions = useMemo(() => {
    return (
      data?.studyPlans?.map((studyPlan) => ({
        label: studyPlan.name,
        value: studyPlan.id,
      })) ?? []
    );
  }, [data]);

  const campusOptions = useMemo(() => {
    return (
      data?.campuses?.map((campus) => ({
        label: campus.name,
        value: campus.id,
      })) ?? []
    );
  }, [data]);

  useEffect(() => {
    if (selectedSchool) {
      updateQuery({ schoolId: selectedSchool.value, ...defaultPagination });
    }
  }, [selectedSchool, updateQuery, defaultPagination]);

  const onSubmit = (data: StudentRiskObservationsFiltersFormFields) => {
    updateQuery({
      schoolId: selectedSchool?.value as number,
      campusId: data.campus?.value as number,
      studyPlanId: data.studyPlan?.value as number,
      student: data.nameOrId as string,
      items_per_page: defaultPagination.items_per_page,
      page: defaultPagination.page,
    });
  };

  const handleClear = () => {
    methods.reset({
      school: selectedSchool,
      nameOrId: null,
      studyPlan: null,
      campus: null,
    });
    updateQuery({
      schoolId: selectedSchool?.value as number,
      campusId: undefined,
      studyPlanId: undefined,
      items_per_page: 5,
      page: 1,
    });
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <Row xs={12}>
        <Col xs={12}>
          <p>{description}</p>
        </Col>
      </Row>
      <Row xs={12}>
        <Col xs={12} lg={4}>
          <Select
            name="school"
            options={schoolOptions}
            label={filters.school.label}
            placeholder={filters.school.placeholder}
            disabled={loading}
            control={methods.control}
          />
        </Col>
      </Row>
      {selectedSchool && (
        <>
          <Row xs={12} className="mt-4">
            <Col xs={12} lg={3}>
              <TextOutlinedInput
                name="nameOrId"
                label={filters.student.label}
                placeholder={filters.student.placeholder}
                disabled={loading}
                control={methods.control}
              />
            </Col>
            <Col xs={12} lg={3}>
              <OutlinedSelect
                name="studyPlan"
                options={studyPlanOptions}
                label={filters.studyPlan.label}
                placeholder={filters.studyPlan.placeholder}
                disabled={loading}
                control={methods.control}
              />
            </Col>
            <Col xs={12} lg={3}>
              <OutlinedSelect
                name="campus"
                options={campusOptions}
                label={filters.campus.label}
                placeholder={filters.campus.placeholder}
                disabled={loading}
                control={methods.control}
              />
            </Col>
            <Col xs={12} lg={3}>
              <OutlinedSelect
                name="graduatedYear"
                options={[]}
                label={filters.graduatedYear.label}
                placeholder={filters.graduatedYear.placeholder}
                disabled={true}
              />
            </Col>
            <Col xs={12} lg={3}>
              <OutlinedSelect
                name="course"
                options={[]}
                label={filters.course.label}
                placeholder={filters.course.placeholder}
                disabled={true}
              />
            </Col>
            <Col xs={12} lg={9}>
              <Row xs={12} className="mt-4 justify-content-end">
                <Col xs={12} lg={4}>
                  <Button
                    type="submit"
                    text={actions.search}
                    size="md"
                    fullwidth
                    color="primary"
                    loading={loading}
                  />
                </Col>
                <Col xs={12} lg={4}>
                  <Button
                    text={actions.clean}
                    size="md"
                    color="primary"
                    fullwidth
                    outlined
                    loading={loading}
                    onClick={handleClear}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}
    </form>
  );
}
